import type { JSX, ReactNode } from 'react';
import { createContext, useContext } from 'react';

import type { DpdpApi } from './api';

/**
 * The one thing every `/dpdp` screen needs from outside React.
 *
 * A context of the feature's own rather than a field on `StaffDeps`: BP-00 R5 lets an app
 * shell reach a feature only through its barrel, so the feature owns its provider and the
 * shell hands it an api built from the shared fetcher. The tests build a fake `DpdpApi`
 * and never touch `fetch`.
 */
export interface DpdpDeps {
  api: DpdpApi;
}

const DpdpDepsContext = createContext<DpdpDeps | null>(null);

/** Make `deps` available to every `/dpdp` screen below. */
export function DpdpDepsProvider({
  deps,
  children,
}: {
  deps: DpdpDeps;
  children: ReactNode;
}): JSX.Element {
  return <DpdpDepsContext.Provider value={deps}>{children}</DpdpDepsContext.Provider>;
}

/** The injected dependencies. Throws when a screen is rendered outside the provider. */
export function useDpdpDeps(): DpdpDeps {
  const deps = useContext(DpdpDepsContext);
  if (deps === null) {
    throw new Error('26AD dpdp: this screen must be rendered inside <DpdpDepsProvider>');
  }
  return deps;
}
